'use client';

import { useQuery } from '@tanstack/react-query';

import { getQueryClient } from 'app/get-query-client';
import { USER_ROLE_LOCAL_STORAGE_KEY } from 'constants/session';
import { useAuthStore } from 'lib/stores/authStore';
import type { ServerError } from 'models';
import type { SsnCollectionMode } from 'models/reqres';
import type { AccessLevel } from 'models/user';
import {
    captureRegistrationSession,
    isRegistrationSessionCurrent,
    useRegistrationStore,
} from 'stores/zustand/registrationStore';
import { updateUserSsn } from 'transport/domains/profile';
import { getUserSignup } from 'transport/domains/signup';
import { noop } from 'utils/noop';
import { setLocalStorageWithExpiration } from 'utils/storage';

export const ssnStatusKeys = {
    all: ['ssnStatus'] as const,
    user: (userId: string) => [...ssnStatusKeys.all, userId] as const,
};

export interface SsnStatus {
    ssnCollectionMode: SsnCollectionMode;
    ssnNeeded: boolean;
}

const defaultSsnStatus: SsnStatus = { ssnCollectionMode: 'none', ssnNeeded: false };

function deriveSsnStatus(mode: SsnCollectionMode): SsnStatus {
    return { ssnCollectionMode: mode, ssnNeeded: mode !== 'none' };
}

// Mirrors UserStore.setAccessLevel — not part of the SSN surface itself, but
// getUserSignup's response carries a fresh access_level that the original
// loadSsnStatus always applied, so this fetcher must too.
function setAccessLevel(val: AccessLevel): void {
    setLocalStorageWithExpiration(USER_ROLE_LOCAL_STORAGE_KEY, val);
    useAuthStore.getState().actions.setAccessLevel(val);
}

function fetchSsnStatus(userId: string): Promise<SsnStatus> {
    // The callback transport ignores TanStack Query cancellation, so removing this
    // query on logout does not stop the response below. Its writes — access level,
    // the registration VO, the SSN mode — would otherwise restore the ended
    // applicant's role and signup state over whoever logged in meanwhile.
    const generation = captureRegistrationSession();
    return new Promise((resolve, reject) => {
        getUserSignup(
            true,
            userId,
            (signupResponse) => {
                if (!isRegistrationSessionCurrent(generation)) {
                    // The query is being torn down; the value is discarded either way.
                    resolve(defaultSsnStatus);
                    return;
                }
                setAccessLevel(signupResponse.access_level);
                const registrationActions = useRegistrationStore.getState().actions;
                registrationActions.updateFromVo(signupResponse.signup);
                // Also writes this query's cache — see registrationStore's setSsnCollectionMode action.
                registrationActions.setSsnCollectionMode(signupResponse.ssn_collection_mode);
                resolve(deriveSsnStatus(signupResponse.ssn_collection_mode));
            },
            reject,
        );
    });
}

export function useSsnStatusQuery() {
    const userId = useAuthStore((s) => s.userId) ?? '';
    const isLoggedInMember = useAuthStore((s) => s.isLoggedInMember);

    return useQuery({
        queryKey: ssnStatusKeys.user(userId),
        queryFn: () => fetchSsnStatus(userId),
        enabled: !!userId && isLoggedInMember,
        staleTime: Infinity,
    });
}

export function useSsnNeeded(): boolean {
    return useSsnStatusQuery().data?.ssnNeeded ?? defaultSsnStatus.ssnNeeded;
}

export function useSsnCollectionMode(): SsnCollectionMode {
    return useSsnStatusQuery().data?.ssnCollectionMode ?? defaultSsnStatus.ssnCollectionMode;
}

/** Imperative read for code that can't use hooks (e.g. a success callback that must react to a just-completed write). */
export function getSsnStatusSnapshot(): SsnStatus {
    const userId = useAuthStore.getState().userId;
    if (!userId) return defaultSsnStatus;
    return getQueryClient().getQueryData<SsnStatus>(ssnStatusKeys.user(userId)) ?? defaultSsnStatus;
}

/** Populate the cache during login bootstrap; errors are swallowed like the original fire-and-forget call. */
export async function ensureSsnStatusLoaded(userId: string): Promise<void> {
    try {
        await getQueryClient().fetchQuery({
            queryKey: ssnStatusKeys.user(userId),
            queryFn: () => fetchSsnStatus(userId),
            staleTime: 0,
        });
    } catch {
        // Non-fatal: consumers fall back to ssnNeeded=false and RQ retries on next mount.
    }
}

/** Re-pulls the fresh SSN status; used after a successful updateSsn to confirm the server's view. */
export function refetchSsnStatus(onSuc: () => void = noop, onErr: () => void = noop): void {
    const userId = useAuthStore.getState().userId ?? '';
    getQueryClient()
        .fetchQuery({ queryKey: ssnStatusKeys.user(userId), queryFn: () => fetchSsnStatus(userId), staleTime: 0 })
        .then(onSuc)
        .catch(onErr);
}

export function updateSsn(
    ssn: string,
    onSuc: () => void = noop,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): void {
    const userId = useAuthStore.getState().userId ?? '';
    updateUserSsn(userId, { ssn }, onSuc, onErr, onFinally);
}

/**
 * Sets the SSN-status cache directly (no fetch) — replaces UserStore's
 * setSsnCollectionMode, used both by consumers reacting to an update error
 * (see UpdateSSNModal) and by registrationStore's own setSsnCollectionMode
 * action as its write-back into this cache.
 */
export function setSsnCollectionMode(mode: SsnCollectionMode): void {
    const userId = useAuthStore.getState().userId;
    if (!userId) return;
    getQueryClient().setQueryData<SsnStatus>(ssnStatusKeys.user(userId), deriveSsnStatus(mode));
}
